// @ts-check
import {InfoWindowContent} from '../InfoWindowContent';
import {DisplayField} from '../DisplayField';
import {getDocument} from '@/services/FirestoreService';
import {capitalize} from '@/lib/ReusableFunctions/Formatters';

/**
 * @typedef {'metro'|'longhaul'} WMTSTileType
 */

let fiberProviders;

/**
 * getFiberProviders - Fetch fiber providers from firestore
 * @return {Promise<Object>} - The fiber providers
 */
async function getProviderConfig() {
  if (fiberProviders) return fiberProviders;
  try {
    fiberProviders = await getDocument('beyondMaps-config', 'fiberProviders');
    return fiberProviders;
  } catch (error) {
    error.message = 'Error fetching fiber providers: ' + error.message;
    console.error(error);
  }
}

/**
 * retrieveProviderColor - Given a provider name, retrieve the color
 * @param {string[]} fiberProviders - The fiber providers returned from firestore
 * @param {string} providerName - The name of the provider
 * @param {string} layerName - The name of the layer
 * @return {string} - The color of the provider
 */
function retrieveProviderColor(fiberProviders, providerName, layerName) {
  if (!fiberProviders[layerName]) return '#000000';
  const provider = fiberProviders[layerName]?.find((provider) => provider.commonName === providerName);
  return provider ? provider.color : '#000000';
}

/**
 * createFiberContentData - Given a fiber wmts tile feature, create content data
 * @param {string} id - The id of the fiber wmts tile
 * @param {string[]} clickedProviders - The names of the providers returned from the fiber click
 * @return {Promise<Object>} - The content data to create info window content with
 */
async function createFiberContentData(id, clickedProviders) {
  try {
    // TODO: make this a general lookup that uses the customFieldMapping option to pick the
    const providerConfig = await getProviderConfig();
    const displayFields = clickedProviders.map((provider) => new DisplayField({
      label: provider,
      unit: 'string',
      value: retrieveProviderColor(providerConfig, provider, id),
      allowEdit: false,
      allowDelete: false,
    }));

    // If there are no display fields, do not open the info window
    if (!displayFields.length) return null;

    return {
      type: 'wmtsTile',
      id,
      title: `Fiber Provider - ${capitalize(id)}`,
      metadata: {},
      displayFields,
    };
  } catch (error) {
    console.warn('Error creating wmtsTile info window content', error);
    return null;
  }
}

/**
 * createWMTSTileContent - Given a type and subjects, create content data
 * @param {WMTSTileType} type - The name of the WMTS Tile layer
 * @param {Object[]} subjects - The subjects to format and display in the info window
 * @return {Promise<InfoWindowContent>} - The content to display in the info window
 */
async function createWMTSTileContent(type, subjects) {
  let contentData;
  if (['metro', 'longhaul'].includes(type)) {
    contentData = await createFiberContentData(type, subjects.map((subject) => subject.common_name));
  } else {
    throw new Error(`createWMTSTileContent: unknown type '${type}'`);
  }
  return new InfoWindowContent(contentData);
}

export {
  createFiberContentData,
  createWMTSTileContent,
};
